import cloneDeep from "lodash/cloneDeep";
import { FractionDenominatorOptions, MissingNumberOptions, MoneyQuestionOptions, Coins, OperationOptions, OrderOfOperationsLevelOptions, AlgebraQuestionOptions, IntegerQuestionOptions, RationalKindOptions, NumberPropertyOptions, ConversionQuestionOptions, PatternQuestionOptions, RomanQuestionOptions, LinePlotQuestionOptions, LinePlotScaleOptions, LinePlotMarkOptions, HistogramQuestionOptions, BoxPlotQuestionOptions, CoordinateQuestionOptions, CoordinateGridOptions, CalendarQuestionOptions, CalendarSpanOptions, CalendarMonthOptions, NumberLineQuestionOptions, PercentQuestionOptions, RatioQuestionOptions, GeometryQuestionOptions, ShapeQuestionOptions, VolumeQuestionOptions, CircleQuestionOptions, ProbabilityQuestionOptions, PythagoreanQuestionOptions, AverageQuestionOptions, BarGraphQuestionOptions, FactFamilyOptions, FractionQuestionOptions, ProblemFormatOptions, PlaceValueOptions, RoundingComparingOptions, RoundingPlaceOptions, RoundingDisplayOptions, TimeQuestionOptions, TimeIntervalOptions, TimeDisplayOptions, Operations, RegroupingOptions, SelectOption, StoryTypeOptions } from "../Constants.ts";
import { decodeSpiralSections } from './spiralSectionsCodec.ts';
import { bucketFor } from "../FactRanges.ts";
import { fitFactLevel } from "../FactLevels.ts";
import { DefaultSettings, ISettingsProps, NumberRange } from "../Models.ts";
import { newSeed } from "../../Shared/Helpers.tsx";

/**
 * The worksheet seed for this load: the `s=` key if the link carried one, a
 * fresh seed otherwise. A link shared before seeding existed has no `s=` and so
 * draws a new seed every visit — which is what that link always meant (ADR 0009 §4).
 */
export function seedFromUrl(queryString: string): string {
    return new URLSearchParams(queryString).get('s') || newSeed();
}

/**
 * Whether a query string is a worksheet link - one `generateUrl` wrote - rather
 * than only a seed, a `uiLang` or a campaign tag.
 *
 * The home page has always decoded any query string it is given. A landing
 * page cannot: it opens with its route's settings, and `?utm_source=...` on it
 * must not swap those for the defaults. So it asks this first. `ppp` and `v1d`
 * are two keys every generated link carries, whatever the format. Links made
 * before the `fmt` key existed only ever pointed at the home page, so they
 * never reach this test.
 */
export function isWorksheetLink(queryString: string): boolean {
    const urlParams = new URLSearchParams(queryString);
    return urlParams.has('ppp') && urlParams.has('v1d');
}

export function ProcessUrl(queryString: string) {
    const urlParams = new URLSearchParams(queryString);
    let initialSettings = JSON.parse(JSON.stringify(DefaultSettings));
    // URLSearchParams.size requires Node 19+ / Chrome 108+; use spread instead.
    if ([...urlParams].length > 0) {
        initialSettings = deconstructURL(urlParams);
    }
    return initialSettings;
}

function deconstructURL(urlParams: URLSearchParams) {
    const add = urlParams.get('Addition');
    const sub = urlParams.get('Subtraction');
    const mul = urlParams.get('Multiplication');
    const div = urlParams.get('Division');
    const lDiv = urlParams.get('Long Division');
    const v1d = urlParams.get('v1d');
    const v1r = urlParams.get('v1r');
    const v2d = urlParams.get('v2d');
    const v2r = urlParams.get('v2r');
    const ppp = urlParams.get('ppp');
    const np = urlParams.get('np');
    const ap = urlParams.get('ap');
    const wk = urlParams.get('wk');
    const cb = urlParams.get('cb');
    const ag = urlParams.get('ag');
    const lp = urlParams.get('lp');
    const sr = urlParams.get('sr');
    const hp = urlParams.get('hp');
    const r = urlParams.get('r');
    const td = urlParams.get('td');
    const fl = urlParams.get('fl');
    const flc = urlParams.get('flc');
    const flk = urlParams.get('flk');
    const wst = urlParams.get('wst');
    const fmt = urlParams.get('fmt');
    const mm = urlParams.get('mm');
    const mq = urlParams.get('mq');
    const mcoins = urlParams.get('mcoins');
    const dp = urlParams.get('dp');
    const dw = urlParams.get('dw');
    const pv = urlParams.get('pv');
    const pvd = urlParams.get('pvd');
    const rc = urlParams.get('rc');
    const rcp = urlParams.get('rcp');
    const rcd = urlParams.get('rcd');
    const rcx = urlParams.get('rcx');
    const rdisp = urlParams.get('rdisp');
    const tq = urlParams.get('tq');
    const ti = urlParams.get('ti');
    const tdisp = urlParams.get('tdisp');
    const noon = urlParams.get('noon');
    const oop = urlParams.get('oop');
    const aq = urlParams.get('aq');
    const amv = urlParams.get('amv');
    const neg = urlParams.get('neg');
    const iq = urlParams.get('iq');
    const imv = urlParams.get('imv');
    const rk = urlParams.get('rk');
    const npq = urlParams.get('npq');
    const npm = urlParams.get('npm');
    const cvq = urlParams.get('cvq');
    const ptq = urlParams.get('ptq');
    const ptl = urlParams.get('ptl');
    const rmq = urlParams.get('rmq');
    const rmm = urlParams.get('rmm');
    const msm = urlParams.get('msm');
    const lpq = urlParams.get('lpq');
    const lps = urlParams.get('lps');
    const lpm = urlParams.get('lpm');
    const hgq = urlParams.get('hgq');
    const bpq = urlParams.get('bpq');
    const cpq = urlParams.get('cpq');
    const cpg = urlParams.get('cpg');
    const calq = urlParams.get('calq');
    const cals = urlParams.get('cals');
    const calm = urlParams.get('calm');
    const nlq = urlParams.get('nlq');
    const nlr = urlParams.get('nlr');
    const pcq = urlParams.get('pcq');
    const pcm = urlParams.get('pcm');
    const gmq = urlParams.get('gmq');
    const gas = urlParams.get('gas');
    // 'Proportional Relationships' was the tables question's value until ADR 0105
    // gave graphs their own and the old name claimed both. Links made before
    // that carry it, so it reads as the tables question (recent worksheets
    // reopen through this too).
    const rtqRaw = urlParams.get('rtq');
    const rtq = rtqRaw === 'Proportional Relationships' ? RatioQuestionOptions.Proportional : rtqRaw;
    const rtm = urlParams.get('rtm');
    const shq = urlParams.get('shq');
    const shm = urlParams.get('shm');
    const vq = urlParams.get('vq');
    const vme = urlParams.get('vme');
    const ciq = urlParams.get('ciq');
    const cir = urlParams.get('cir');
    const prq = urlParams.get('prq');
    const pyq = urlParams.get('pyq');
    const pym = urlParams.get('pym');
    const avq = urlParams.get('avq');
    const avl = urlParams.get('avl');
    const bgq = urlParams.get('bgq');
    const bgm = urlParams.get('bgm');
    const bqc = urlParams.get('bqc');
    const ff = urlParams.get('ff');
    const fq = urlParams.get('fq');
    const fmd = urlParams.get('fmd');
    const lang = urlParams.get('lang');
    // Superseded by fmt, still read so links shared before the change work.
    const legacyWordProblems = urlParams.get('wp');
    const legacyFractions = urlParams.get('fr');
    const legacyMoney = urlParams.get('mn');
    const fd = urlParams.get('fd');
    const md = urlParams.get('md');
    const rg = urlParams.get('rg');
    const mnum = urlParams.get('mnum');
    const ds = urlParams.get('ds');
    // Only the class-set mode is read. There is deliberately no roster key -
    // student names live in component state and never touch the URL. ADR 0011 §1.
    const cs = urlParams.get('cs');

    let operators: SelectOption[] = [];
    if (add === '1') {
        operators.push(OperationOptions.filter(op => op.value === Operations.Addition)[0]);
    }
    if (sub === '1') {
        operators.push(OperationOptions.filter(op => op.value === Operations.Subtraction)[0]);
    }
    if (mul === '1') {
        operators.push(OperationOptions.filter(op => op.value === Operations.Multiplication)[0]);
    }
    if (div === '1') {
        operators.push(OperationOptions.filter(op => op.value === Operations.Division)[0]);
    }
    if (lDiv === '1') {
        operators.push(OperationOptions.filter(op => op.value === Operations.LongDivision)[0]);
    }
    const nr1Digits = v1d ? parseInt(v1d) : 1;
    let range1 = setNumberRange(nr1Digits, operators);
    let numberRanges: NumberRange[] = [];
    let numberRange1: NumberRange = {
        Id: 1,
        NumberOfDigits: nr1Digits,
        NumberRange: range1,
        NumberRangeSelection: 
            v1r ? 
            v1r!.split(',').map((number) => parseInt(number)) :
            DefaultSettings.NumberRanges[0].NumberRangeSelection
    };
    numberRanges.push(numberRange1);
    

    const nr2Digits = v2d ? parseInt(v2d) : 1;
    let range2 = setNumberRange(nr2Digits, operators);
    let numberRange2: NumberRange = {
        Id: 2,
        NumberOfDigits: nr2Digits,
        NumberRange: range2,
        NumberRangeSelection: 
            v2r ? 
            v2r!.split(',').map((number) => parseInt(number)) :
            DefaultSettings.NumberRanges[1].NumberRangeSelection
    };
    numberRanges.push(numberRange2);


    /*
     * Built by overriding a copy of the defaults rather than as a literal.
     *
     * As a literal this quietly omitted Page and AllOperations - neither has a
     * URL key - and MathFactsGenerator reads `settings.AllOperations ? "Math
     * Facts" : settings.Page` to title the sheet. Undefined fell through to an
     * undefined Page, .toLowerCase() threw, and every shared worksheet link
     * rendered a blank page. Spreading the defaults means a field without a key
     * keeps its default instead of going missing, now and for whatever gets
     * added next.
     */
    let urlSettings: ISettingsProps = {
        ...cloneDeep(DefaultSettings),
        Operators: operators,
        NumberOfProblemsPerPage: ppp && !isNaN(parseInt(ppp)) ? parseInt(ppp) : 30,
        NumberOfPages: np && !isNaN(parseInt(np!)) ? parseInt(np!) : 1,
        NumberRanges: numberRanges,
        AnswerPages: ap === '1' ? true : false,
        WorkedAnswers: wk === '1',
        Presentation: {
            carryBoxes: cb === '1',
            alignmentGrid: ag === '1',
            largePrint: lp === '1',
        },
        SpiralSections: sr
            ? decodeSpiralSections(sr)
            : DefaultSettings.SpiralSections,
        HalfPage: hp === '1' ? true : false,
        Remainders: r === '1' ? true : false,
        Timed: td === '1' ? true : false,
        FactLevel: factLevelFrom(fl),
        FactLevelCeiling: flc === '12' ? 12 : 10,
        // Only `p` means practice; anything else, and no key, is the test.
        FactLevelSheet: flk === 'p' ? 'practice' : 'test',
        // Only `v` means Varied; anything else, and no key, is the ordinary sheet (ADR 0073).
        StoryTypes: wst === 'v' ? StoryTypeOptions.Varied : StoryTypeOptions.Simple,
        Format: formatFrom(fmt, legacyWordProblems, legacyFractions, legacyMoney),
        TimeQuestion:
            Object.values(TimeQuestionOptions).find((option) => option === tq) ??
            DefaultSettings.TimeQuestion,
        TimeInterval:
            Object.values(TimeIntervalOptions).find((option) => option === ti) ??
            DefaultSettings.TimeInterval,
        TimeDisplay:
            Object.values(TimeDisplayOptions).find((option) => option === tdisp) ??
            DefaultSettings.TimeDisplay,
        CrossNoon: noon === '1',
        ShapeQuestion:
            Object.values(ShapeQuestionOptions).find((option) => option === shq) ??
            DefaultSettings.ShapeQuestion,
        ShapeMaxSide:
            shm && !isNaN(parseInt(shm)) ? parseInt(shm) : DefaultSettings.ShapeMaxSide,
        VolumeQuestion:
            Object.values(VolumeQuestionOptions).find((option) => option === vq) ??
            DefaultSettings.VolumeQuestion,
        VolumeMaxEdge:
            vme && !isNaN(parseInt(vme)) ? parseInt(vme) : DefaultSettings.VolumeMaxEdge,
        CircleQuestion:
            Object.values(CircleQuestionOptions).find((option) => option === ciq) ??
            DefaultSettings.CircleQuestion,
        CircleMaxRadius:
            cir && !isNaN(parseInt(cir)) ? parseInt(cir) : DefaultSettings.CircleMaxRadius,
        ProbabilityQuestion:
            Object.values(ProbabilityQuestionOptions).find((option) => option === prq) ??
            DefaultSettings.ProbabilityQuestion,
        PythagoreanQuestion:
            Object.values(PythagoreanQuestionOptions).find((option) => option === pyq) ??
            DefaultSettings.PythagoreanQuestion,
        PythagoreanMaxSide:
            pym && !isNaN(parseInt(pym)) ? parseInt(pym) : DefaultSettings.PythagoreanMaxSide,
        AverageQuestion:
            Object.values(AverageQuestionOptions).find((option) => option === avq) ??
            DefaultSettings.AverageQuestion,
        AverageListLength:
            avl && !isNaN(parseInt(avl)) ? parseInt(avl) : DefaultSettings.AverageListLength,
        BarGraphQuestion:
            Object.values(BarGraphQuestionOptions).find((option) => option === bgq) ??
            DefaultSettings.BarGraphQuestion,
        BarGraphMaxValue:
            bgm && !isNaN(parseInt(bgm)) ? parseInt(bgm) : DefaultSettings.BarGraphMaxValue,
        BarGraphQuestionsPerChart:
            bqc && !isNaN(parseInt(bqc)) ? parseInt(bqc) : DefaultSettings.BarGraphQuestionsPerChart,
        GeometryQuestion:
            Object.values(GeometryQuestionOptions).find((option) => option === gmq) ??
            DefaultSettings.GeometryQuestion,
        GeometryAngleStep:
            gas && !isNaN(parseInt(gas)) ? parseInt(gas) : DefaultSettings.GeometryAngleStep,
        RatioQuestion:
            Object.values(RatioQuestionOptions).find((option) => option === rtq) ??
            DefaultSettings.RatioQuestion,
        RatioMaxValue:
            rtm && !isNaN(parseInt(rtm)) ? parseInt(rtm) : DefaultSettings.RatioMaxValue,
        PercentQuestion:
            Object.values(PercentQuestionOptions).find((option) => option === pcq) ??
            DefaultSettings.PercentQuestion,
        PercentMaxValue:
            pcm && !isNaN(parseInt(pcm)) ? parseInt(pcm) : DefaultSettings.PercentMaxValue,
        NumberLineQuestion:
            Object.values(NumberLineQuestionOptions).find((option) => option === nlq) ??
            DefaultSettings.NumberLineQuestion,
        NumberLineRange:
            nlr && !isNaN(parseInt(nlr)) ? parseInt(nlr) : DefaultSettings.NumberLineRange,
        PatternQuestion:
            Object.values(PatternQuestionOptions).find((option) => option === ptq) ??
            DefaultSettings.PatternQuestion,
        PatternLength:
            ptl && !isNaN(parseInt(ptl)) ? parseInt(ptl) : DefaultSettings.PatternLength,
        RomanQuestion:
            Object.values(RomanQuestionOptions).find((option) => option === rmq) ??
            DefaultSettings.RomanQuestion,
        RomanMax:
            rmm && !isNaN(parseInt(rmm)) ? parseInt(rmm) : DefaultSettings.RomanMax,
        MagicSquareMax:
            msm && !isNaN(parseInt(msm)) ? parseInt(msm) : DefaultSettings.MagicSquareMax,
        LinePlotQuestion:
            Object.values(LinePlotQuestionOptions).find((option) => option === lpq) ??
            DefaultSettings.LinePlotQuestion,
        LinePlotScale:
            Object.values(LinePlotScaleOptions).find((option) => option === lps) ??
            DefaultSettings.LinePlotScale,
        LinePlotMark:
            Object.values(LinePlotMarkOptions).find((option) => option === lpm) ??
            DefaultSettings.LinePlotMark,
        HistogramQuestion:
            Object.values(HistogramQuestionOptions).find((option) => option === hgq) ??
            DefaultSettings.HistogramQuestion,
        BoxPlotQuestion:
            Object.values(BoxPlotQuestionOptions).find((option) => option === bpq) ??
            DefaultSettings.BoxPlotQuestion,
        CoordinateQuestion:
            Object.values(CoordinateQuestionOptions).find((option) => option === cpq) ??
            DefaultSettings.CoordinateQuestion,
        CoordinateGrid:
            Object.values(CoordinateGridOptions).find((option) => option === cpg) ??
            DefaultSettings.CoordinateGrid,
        CalendarQuestion:
            Object.values(CalendarQuestionOptions).find((option) => option === calq) ??
            DefaultSettings.CalendarQuestion,
        CalendarSpan:
            Object.values(CalendarSpanOptions).find((option) => option === cals) ??
            DefaultSettings.CalendarSpan,
        CalendarMonth:
            Object.values(CalendarMonthOptions).find((option) => option === calm) ??
            DefaultSettings.CalendarMonth,
        ConversionQuestion:
            Object.values(ConversionQuestionOptions).find((option) => option === cvq) ??
            DefaultSettings.ConversionQuestion,
        NumberPropertyQuestion:
            Object.values(NumberPropertyOptions).find((option) => option === npq) ??
            DefaultSettings.NumberPropertyQuestion,
        NumberPropertyMaxValue:
            npm && !isNaN(parseInt(npm)) ? parseInt(npm) : DefaultSettings.NumberPropertyMaxValue,
        IntegerQuestion:
            Object.values(IntegerQuestionOptions).find((option) => option === iq) ??
            DefaultSettings.IntegerQuestion,
        IntegerMaxValue:
            imv && !isNaN(parseInt(imv)) ? parseInt(imv) : DefaultSettings.IntegerMaxValue,
        // A link made before the rational types has no `rk`; it reads as Both.
        RationalKind:
            Object.values(RationalKindOptions).find((option) => option === rk) ??
            DefaultSettings.RationalKind,
        AllowNegatives: neg === '1',
        AlgebraQuestion:
            Object.values(AlgebraQuestionOptions).find((option) => option === aq) ??
            DefaultSettings.AlgebraQuestion,
        AlgebraMaxValue:
            amv && !isNaN(parseInt(amv)) ? parseInt(amv) : DefaultSettings.AlgebraMaxValue,
        OrderOfOperationsLevel:
            Object.values(OrderOfOperationsLevelOptions).find((option) => option === oop) ??
            DefaultSettings.OrderOfOperationsLevel,
        FactFamilyQuestion:
            Object.values(FactFamilyOptions).find((option) => option === ff) ??
            DefaultSettings.FactFamilyQuestion,
        FractionQuestion:
            Object.values(FractionQuestionOptions).find((option) => option === fq) ??
            DefaultSettings.FractionQuestion,
        FractionModelMaxDenominator:
            fmd && !isNaN(parseInt(fmd)) ? parseInt(fmd) : DefaultSettings.FractionModelMaxDenominator,
        // ADR 0051 §7: widened to 'fr' alongside 'es'; anything else - a
        // missing key, or a value from before 'fr' existed - is 'en'.
        Language: lang === 'es' || lang === 'fr' ? lang : 'en',
        PlaceValueQuestion:
            Object.values(PlaceValueOptions).find((option) => option === pv) ??
            DefaultSettings.PlaceValueQuestion,
        PlaceValueDigits:
            pvd && !isNaN(parseInt(pvd)) ? parseInt(pvd) : DefaultSettings.PlaceValueDigits,
        RoundingComparingQuestion:
            Object.values(RoundingComparingOptions).find((option) => option === rc) ??
            DefaultSettings.RoundingComparingQuestion,
        RoundingPlace:
            Object.values(RoundingPlaceOptions).find((option) => option === rcp) ??
            DefaultSettings.RoundingPlace,
        RoundingComparingDigits:
            rcd && !isNaN(parseInt(rcd)) ? parseInt(rcd) : DefaultSettings.RoundingComparingDigits,
        RoundingComparingDecimals:
            rcx !== null && !isNaN(parseInt(rcx))
                ? parseInt(rcx)
                : DefaultSettings.RoundingComparingDecimals,
        RoundingDisplay:
            Object.values(RoundingDisplayOptions).find((option) => option === rdisp) ??
            DefaultSettings.RoundingDisplay,
        MoneyMaxDollars: mm && !isNaN(parseInt(mm)) ? parseInt(mm) : DefaultSettings.MoneyMaxDollars,
        MoneyQuestion:
            Object.values(MoneyQuestionOptions).find((option) => option === mq) ??
            DefaultSettings.MoneyQuestion,
        MoneyCoins: mcoins
            ? mcoins.split(',').map((name) => name.trim()).filter((name) =>
                  Coins.some((coin) => coin.name === name)
              )
            : DefaultSettings.MoneyCoins,
        DecimalPlaces: dp && !isNaN(parseInt(dp)) ? parseInt(dp) : DefaultSettings.DecimalPlaces,
        DecimalMaxWhole: dw && !isNaN(parseInt(dw)) ? parseInt(dw) : DefaultSettings.DecimalMaxWhole,
        FractionDenominators: denominatorRuleFrom(fd),
        MaxDenominator: md && !isNaN(parseInt(md)) ? parseInt(md) : DefaultSettings.MaxDenominator,
        Regrouping: rg ? RegroupingOptions[rg.replace(/\s+/g, '')] : DefaultSettings.Regrouping,
        MissingNumber:
            Object.values(MissingNumberOptions).find((option) => option === mnum) ??
            DefaultSettings.MissingNumber,
        DifferentiationSet: ds === '1',
        ClassSetDistinctProblems: cs === '1',
    };

    // A hand-edited fl=99 lands on level 1 of the ladder rather than on a
    // level that does not exist (ADR 0071 §5).
    return fitFactLevel(urlSettings);
}

/** A level is a whole number; anything else in `fl` means no level. */
function factLevelFrom(fl: string | null): number {
    const level = fl === null ? NaN : Number(fl);
    return Number.isInteger(level) && level > 0 ? level : 0;
}

/**
 * The sheet format. `fmt` carries it directly; the wp/fr/mn flags are what
 * older shared links used, and are honoured so those links still open the
 * worksheet they were made for.
 */
function formatFrom(
    fmt: string | null,
    wordProblems: string | null,
    fractions: string | null,
    money: string | null
): ProblemFormatOptions {
    const named = Object.values(ProblemFormatOptions).find((option) => option === fmt);
    if (named) {
        return named;
    }
    if (fractions === '1') {
        return ProblemFormatOptions.Fractions;
    }
    if (money === '1') {
        return ProblemFormatOptions.Money;
    }
    if (wordProblems === '1') {
        return ProblemFormatOptions.WordProblem;
    }
    return DefaultSettings.Format;
}

/** Decodes the denominator rule, which travels as its own display text. */
function denominatorRuleFrom(value: string | null): FractionDenominatorOptions {
    const match = Object.values(FractionDenominatorOptions).find((option) => option === value);
    return match ?? DefaultSettings.FractionDenominators;
}

/**
 * The numbers an operand's range offers. The operations matter as well as the
 * digit count: a multiplication/division sheet's one-digit range is 0-12
 * (ADR 0064). An old link's selection is still inside it, so it decodes to the
 * sheet it always drew.
 */
function setNumberRange(NumberOfDigits: number, operators: SelectOption[]) {
    return [...bucketFor(NumberOfDigits, operators.map((op) => op.value)).Range];
}